<script setup lang="ts">
import { ref, watch, onMounted, nextTick, computed } from 'vue'
import mermaid from 'mermaid'
import { torcazaMermaidConfig } from '@/config/mermaidTheme'
import { Code2, AlertCircle, Download, Image as ImageIcon, ZoomIn, ZoomOut, Maximize, Expand } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'

const props = defineProps<{
  code: string
}>()

const svgContent = ref('')
const error = ref('')
const fullscreenContainer = ref<HTMLElement | null>(null)

let renderTimeout: ReturnType<typeof setTimeout> | null = null

const renderMermaid = async () => {
  if (!props.code || !props.code.trim()) {
    svgContent.value = ''
    error.value = ''
    return
  }
  
  let rawCode = props.code
  const match = props.code.match(/```mermaid\n([\s\S]*?)```/)
  if (match) {
    rawCode = match[1]
  } else {
    rawCode = props.code.replace(/```mermaid\n?/g, '').replace(/```\n?/g, '')
  }
  
  try {
    error.value = ''
    const id = `mermaid-render-${Math.random().toString(36).substr(2, 9)}`
    const { svg } = await mermaid.render(id, rawCode)
    svgContent.value = svg
  } catch (e: any) {
    console.error('Mermaid render error', e)
    error.value = e.message || 'Sintaxis de Mermaid inválida'
  }
}

const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    fullscreenContainer.value?.requestFullscreen().catch(err => {
      console.error(`Error entering fullscreen: ${err.message}`);
    });
  } else {
    document.exitFullscreen();
  }
}

const exportSVG = () => {
  if (!svgContent.value) return
  const blob = new Blob([svgContent.value], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `diagrama-${Date.now()}.svg`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const exportPNG = () => {
  if (!svgContent.value) return
  const svg = document.querySelector('.mermaid-container svg') as SVGSVGElement
  if (!svg) return
  
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  
  const svgClone = svg.cloneNode(true) as SVGSVGElement
  const bbox = svg.getBoundingClientRect()
  svgClone.setAttribute('width', `${bbox.width}`)
  svgClone.setAttribute('height', `${bbox.height}`)
  
  const svgData = new XMLSerializer().serializeToString(svgClone)
  
  // Use base64 data URL instead of Blob to avoid Tainted Canvas SecurityError
  const base64SVG = window.btoa(unescape(encodeURIComponent(svgData)))
  const url = `data:image/svg+xml;base64,${base64SVG}`
  
  const img = new Image()
  img.crossOrigin = 'anonymous'
  
  img.onload = () => {
    canvas.width = bbox.width * 2 // Escala x2 para alta resolución
    canvas.height = bbox.height * 2
    
    if (ctx) {
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      
      try {
        const pngUrl = canvas.toDataURL('image/png')
        const a = document.createElement('a')
        a.href = pngUrl
        a.download = `diagrama-${Date.now()}.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (err) {
        console.error('Error al exportar PNG (posible Canvas Tainted):', err)
        error.value = 'No se pudo exportar a PNG por restricciones de seguridad del navegador. Intenta exportar como SVG.'
      }
    }
  }
  
  img.src = url
}

watch(() => props.code, () => {
  if (renderTimeout) clearTimeout(renderTimeout)
  renderTimeout = setTimeout(() => {
    renderMermaid()
  }, 400)
}, { immediate: true })

// --- PAN AND ZOOM LOGIC ---
const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
let isDragging = false
let startX = 0
let startY = 0

const onWheel = (e: WheelEvent) => {
  if (!svgContent.value) return
  const zoomIntensity = 0.1
  const delta = e.deltaY > 0 ? -zoomIntensity : zoomIntensity
  scale.value = Math.min(Math.max(0.1, scale.value + delta), 5)
}

const onMouseDown = (e: MouseEvent) => {
  if (!svgContent.value) return
  isDragging = true
  startX = e.clientX - translateX.value
  startY = e.clientY - translateY.value
}

const onMouseMove = (e: MouseEvent) => {
  if (!isDragging) return
  translateX.value = e.clientX - startX
  translateY.value = e.clientY - startY
}

const onMouseUp = () => {
  isDragging = false
}

const zoomIn = () => scale.value = Math.min(scale.value + 0.2, 5)
const zoomOut = () => scale.value = Math.max(scale.value - 0.2, 0.1)
const resetZoom = () => {
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
}

const containerStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  transformOrigin: 'center center',
  width: '100%',
  height: '100%',
  transition: isDragging ? 'none' : 'transform 0.1s ease-out'
}))

onMounted(() => {
  mermaid.initialize(torcazaMermaidConfig)
  nextTick(() => {
    renderMermaid()
  })
})
</script>

<template>
  <div class="relative w-full h-full flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden" ref="fullscreenContainer">
    <div class="p-2 border-b bg-muted/40 text-xs font-bold text-muted-foreground shrink-0 shadow-sm z-10 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <span>Previsualización</span>
        <div class="flex gap-1" v-if="svgContent && !error">
          <Button size="icon" variant="ghost" class="h-6 w-6 text-muted-foreground hover:text-primary" @click="toggleFullscreen" title="Pantalla Completa (Presentación)">
            <Expand class="w-3 h-3" />
          </Button>
          <Button size="icon" variant="ghost" class="h-6 w-6 text-muted-foreground hover:text-primary" @click="zoomIn" title="Acercar">
            <ZoomIn class="w-3 h-3" />
          </Button>
          <Button size="icon" variant="ghost" class="h-6 w-6 text-muted-foreground hover:text-primary" @click="zoomOut" title="Alejar">
            <ZoomOut class="w-3 h-3" />
          </Button>
          <Button size="icon" variant="ghost" class="h-6 w-6 text-muted-foreground hover:text-primary mr-2" @click="resetZoom" title="Restaurar vista">
            <Maximize class="w-3 h-3" />
          </Button>

          <Button size="sm" variant="outline" class="h-6 text-[10px] px-2 py-0 border-primary/20 hover:border-primary" @click="exportSVG">
            <Download class="w-3 h-3 mr-1" /> SVG
          </Button>
          <Button size="sm" variant="outline" class="h-6 text-[10px] px-2 py-0 border-primary/20 hover:border-primary" @click="exportPNG">
            <ImageIcon class="w-3 h-3 mr-1" /> PNG
          </Button>
        </div>
      </div>
      <span v-if="error" class="text-destructive flex items-center gap-1"><AlertCircle class="w-3 h-3"/> Error de sintaxis</span>
    </div>
    
    <!-- CONTENEDOR CON PAN & ZOOM -->
    <div 
      class="flex-1 overflow-hidden p-4 flex items-center justify-center relative cursor-grab active:cursor-grabbing"
      @wheel.prevent="onWheel"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
    >
      <div 
        v-if="svgContent && !error" 
        v-html="svgContent" 
        class="max-w-full max-h-full flex items-center justify-center mermaid-container"
        :style="containerStyle"
      ></div>
      <div v-else-if="!svgContent && !error" class="text-muted-foreground flex items-center gap-2">
        <Code2 class="w-8 h-8 opacity-20" />
        <span class="opacity-50 text-sm">Esperando código...</span>
      </div>
      
      <!-- Si hay error y había SVG previo, mantenemos el SVG opaco y mostramos el error como alerta -->
      <div 
        v-if="error && svgContent" 
        v-html="svgContent" 
        class="max-w-full max-h-full flex items-center justify-center mermaid-container opacity-30 grayscale"
        :style="containerStyle"
      ></div>
    </div>
  </div>
</template>

<style scoped>
:deep(svg) {
  max-width: 100%;
  max-height: 100%;
}
</style>
